import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { DataView } from '@/lib/ui/states';
import { Button } from '@/lib/ui/Button';
import { MonoValue } from '@/lib/ui/Typography';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { useToast } from '@/lib/ui';
import { useApiError } from '@/lib/query/useApiError';
import { useGate } from '@/lib/rbac/Gate';
import { formatDateTime } from '@/lib/format';
import type { Revision } from '@/lib/api/types';

/**
 * Per-entity revision history (spec §14). Restore re-applies editorial fields as a NEW
 * revision and never changes publication status (gap 14). Diff view compares two snapshots.
 */
export function RevisionsTab({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const { allowed: canWrite } = useGate('content:write');
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [compare, setCompare] = useState<[string?, string?]>([]);

  const query = useQuery({
    queryKey: keys.revisions(entityType, entityId),
    queryFn: () => api.get<Revision[]>('/admin/revisions', { entity_type: entityType, entity_id: entityId }),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/admin/revisions/${id}/restore`),
    onSuccess: () => {
      void qc.invalidateQueries();
      setRestoreId(null);
      toast.success(t('revisions.restoredToast'), t('revisions.restoredToastDesc'));
    },
    onError,
  });

  return (
    <DataView query={query} isEmpty={(d) => d.length === 0}>
      {(revisions) => (
        <div className="flex flex-col gap-sm">
          <ul className="divide-y divide-hairline rounded-md border-[0.5px] border-hairline">
            {revisions.map((r) => {
              const selected = compare.includes(r.id);
              return (
                <li key={r.id} className="flex items-center justify-between gap-md px-md py-sm">
                  <label className="flex items-center gap-sm">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        setCompare((prev) => {
                          if (prev.includes(r.id)) return prev.filter((x) => x !== r.id) as [string?, string?];
                          return [prev[1], r.id];
                        })
                      }
                      className="accent-accent-blue"
                    />
                    <span className="text-small">
                      <span className="font-mono text-ink-deep">
                        #<MonoValue>{r.revision_number ?? '—'}</MonoValue>
                      </span>{' '}
                      <span className="text-ink-mute">{formatDateTime(r.created_at)}</span>
                    </span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canWrite}
                    onClick={() => setRestoreId(r.id)}
                  >
                    {t('revisions.restore')}
                  </Button>
                </li>
              );
            })}
          </ul>

          {compare[0] && compare[1] && (
            <RevisionDiff
              a={revisions.find((r) => r.id === compare[0])!}
              b={revisions.find((r) => r.id === compare[1])!}
            />
          )}

          <ConfirmDialog
            open={!!restoreId}
            onOpenChange={(o) => !o && setRestoreId(null)}
            title={t('revisions.restoreDialogTitle')}
            description={t('revisions.restoreDialogDesc')}
            confirmLabel={t('revisions.restore')}
            loading={restore.isPending}
            onConfirm={() => restoreId && restore.mutate(restoreId)}
          />
        </div>
      )}
    </DataView>
  );
}

function RevisionDiff({ a, b }: { a: Revision; b: Revision }) {
  const left = JSON.stringify(a.snapshot, null, 2);
  const right = JSON.stringify(b.snapshot, null, 2);
  return (
    <div className="grid grid-cols-2 gap-sm">
      {[a, b].map((r, i) => (
        <div key={r.id} className="rounded-md border-[0.5px] border-hairline bg-canvas-bone p-sm">
          <p className="mb-xs font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
            #{r.revision_number ?? '—'}
          </p>
          <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-all font-mono text-label text-ink-graphite">
            {i === 0 ? left : right}
          </pre>
        </div>
      ))}
    </div>
  );
}
